import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Check,
  X,
  Clock,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { api } from '@/lib/api';
import type { GeneralRequestForm, GeneralRequestSubmission } from '@/types';

export default function FormSubmissions() {
  const { venueId, formId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expandedSubmission, setExpandedSubmission] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');

  const { data: form } = useQuery<GeneralRequestForm>({
    queryKey: ['form', formId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/forms/${formId}`);
      return response.data;
    },
  });

  const { data: submissions, isLoading } = useQuery<GeneralRequestSubmission[]>({
    queryKey: ['form-submissions', formId, filter],
    queryFn: async () => {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await api.get(`/admin/venues/${venueId}/forms/${formId}/submissions${params}`);
      return response.data;
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ submissionId, status, notes }: { submissionId: string; status: string; notes?: string }) => {
      await api.put(`/admin/venues/${venueId}/forms/${formId}/submissions/${submissionId}`, {
        status,
        admin_notes: notes,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-submissions', formId] });
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="h-3 w-3" />
            Pending
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <Check className="h-3 w-3" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <X className="h-3 w-3" />
            Rejected
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Cancelled
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatValue = (value: unknown, _fieldType: string): string => {
    if (value === null || value === undefined) return '-';

    if (Array.isArray(value)) {
      return value.join(', ');
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/venues/${venueId}/forms`)}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Form Submissions</h1>
          <p className="text-sm text-gray-500">{form?.name}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-6">
        <span className="text-sm text-gray-500">Filter:</span>
        {(['all', 'pending', 'approved', 'rejected'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-3 py-1.5 text-sm rounded-md ${
              filter === status
                ? 'bg-indigo-100 text-indigo-700 font-medium'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      <div className="card">
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                </div>
              </div>
            ))}
          </div>
        ) : submissions?.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No submissions found.</p>
            <p className="text-sm">Submissions will appear here when users fill out the form.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {submissions?.map((submission) => (
              <div
                key={submission.id}
                className="border rounded-lg overflow-hidden"
              >
                <div
                  className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() =>
                    setExpandedSubmission(
                      expandedSubmission === submission.id ? null : submission.id
                    )
                  }
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {submission.first_name || submission.user_email || 'Anonymous'}
                        {submission.last_name && ` ${submission.last_name}`}
                      </span>
                      {getStatusBadge(submission.status)}
                    </div>
                    <p className="text-sm text-gray-500">
                      Submitted {formatDate(submission.submitted_at)}
                    </p>
                  </div>

                  {submission.status === 'pending' && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateStatusMutation.mutate({
                            submissionId: submission.id,
                            status: 'approved',
                          });
                        }}
                        className="p-2 bg-green-100 text-green-700 rounded-md hover:bg-green-200"
                        title="Approve"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateStatusMutation.mutate({
                            submissionId: submission.id,
                            status: 'rejected',
                          });
                        }}
                        className="p-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                        title="Reject"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}

                  {expandedSubmission === submission.id ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </div>

                {expandedSubmission === submission.id && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Submission Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {submission.values?.map((val) => (
                        <div key={val.id} className="bg-white p-3 rounded-md border border-gray-200">
                          <label className="text-xs text-gray-500 uppercase">{val.field_label}</label>
                          <p className="text-sm font-medium mt-1">
                            {formatValue(val.value_json || val.value, val.field_type)}
                          </p>
                        </div>
                      ))}
                    </div>

                    {submission.admin_notes && (
                      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <label className="text-xs text-yellow-700 uppercase">Admin Notes</label>
                        <p className="text-sm mt-1">{submission.admin_notes}</p>
                      </div>
                    )}

                    {submission.reviewed_at && (
                      <p className="text-xs text-gray-500 mt-4">
                        Reviewed by {submission.reviewer_first_name || submission.reviewer_email} on{' '}
                        {formatDate(submission.reviewed_at)}
                      </p>
                    )}

                    {submission.status === 'pending' && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Add Notes (optional)
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            id={`notes-${submission.id}`}
                            className="input flex-1"
                            placeholder="Add notes before approving/rejecting..."
                          />
                          <button
                            onClick={() => {
                              const notes = (
                                document.getElementById(`notes-${submission.id}`) as HTMLInputElement
                              )?.value;
                              updateStatusMutation.mutate({
                                submissionId: submission.id,
                                status: 'approved',
                                notes,
                              });
                            }}
                            className="btn btn-primary"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => {
                              const notes = (
                                document.getElementById(`notes-${submission.id}`) as HTMLInputElement
                              )?.value;
                              updateStatusMutation.mutate({
                                submissionId: submission.id,
                                status: 'rejected',
                                notes,
                              });
                            }}
                            className="btn bg-red-600 text-white hover:bg-red-700"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
